import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as excel from '@attain-apps/fe/web/main/lib/excel'
import { TypeAsserter } from '@attain-apps/type-guards'

import * as state from '../../../../../../state'

import * as components from './components'

@ngCore.Component({
  imports: [
    ngCommon.NgIf,
    ds.button.Component,
    ds.layout.page.child.header.Component,
    ds.layout.page.child.header.child.Pagination.Component,
    ds.layout.page.Component,
    components.Table.Component,
  ],
  selector: 'atn-rate-report-page',
  standalone: true,
  styleUrls: ['./page.component.scss'],
  templateUrl: './page.component.html',
})
export class AtnReportDetailsComponent
  implements ngCore.OnInit, ngCore.OnDestroy
{
  /** @see {@link ds.Color} */
  public readonly PredefinedStyleEnum = ds.color.Legacy.PredefinedStyle

  private readonly _meFeature = ngCore.inject(state.me.Feature)

  private readonly _reportsEc = ngCore.inject(state.rate.reports.Collection)

  public readonly report =
    this._reportsEc.plugins.currentEntityPlugin.feature.selectors.getEntity()
      .asSignal
  public readonly reportPlugin = this._reportsEc.plugins
    .lookup(state.rate.reports.plugins.content.Plugin)
    .get()

  /**
   * Total count for reports
   *
   * @default 0
   */
  public readonly numberOfPagesSg = ngCore.computed(
    (
      totalCount = this._reportsEc.plugins.currentListPlugin.feature.selectors.matchedCount.asSignal(),
    ) => totalCount ?? 0,
  )

  /**
   * By default, this will be undefined if the selected report is not found in
   * the list. If it is found, it returns the selected index incremented by one,
   * as the selected pages begin counting from 1 rather than 0.
   */
  public readonly reportIdxSg =
    this._reportsEc.plugins.currentEntityPlugin.feature.selectors.getPosition()
      .asSignal

  /**
   *  Excel plugin for Report entity collection
   */
  private readonly _excelEcPlugin = this._reportsEc.plugins
    .lookup(excel.ec.Plugin)
    .get()

  public readonly shouldShowDownloadButtonSg = ngCore.computed(
    () => !!this._excelEcPlugin,
  )

  public readonly breadcrumbsSg: ngCore.Signal<
    ds.layout.page.child.header.models.Breadcrumb[]
  > = ngCore.computed(() => {
    const report = this._reportsEc.plugins.currentEntityPlugin.feature.selectors
      .getEntity()
      .asSignal()
    const reportCode = report?.label.code
    if (report && reportCode) {
      return [
        {
          callback: () => {
            const source = 'ReportDetails.Breadcrumbs.Click'
            TypeAsserter.assertIsNotNullish(report)

            // TODO: what is this?
            this._reportsEc.plugins.selectionPlugin.feature.actions.discardEdits
              .from(source)
              .dispatch({
                target: report.label.id,
              })
          },
          name: 'Reports',
          url: this._meFeature.contexts.selectors
            .getUrlForLink({
              goto: {
                navigationId: ents.extra.prebuilt.AppPageAlias.Reports,
              },
            })
            .asSignal(),
        },
        {
          name: reportCode,
        },
      ]
    } else {
      return []
    }
  })

  ngOnInit(): void {
    /**
     * Note: This action dispatch is necessary to handle page refreshes.
     *
     * While the rate-ec.service has an effect that dispatches an action
     * to provide content for the selected report, it doesn't work on page refresh.
     * This additional dispatch ensures the report content is fetched in all scenarios.
     */
    // TODO: reevaluate the possibility of implementing this in new plugin(s)
    // this._selectionPlugin.dispatchGetOneForSelectedEntity(
    //   'ReportDetails.Init',
    //   { useCache: entity => !!entity?.content },
    // )
  }

  ngOnDestroy(): void {
    const report = this._reportsEc.plugins.currentEntityPlugin.feature.selectors
      .getEntity()
      .asSignal()
    if (report) {
      this._reportsEc.plugins.selectionPlugin.feature.actions.discardEdits
        .from('ReportDetailsComponent.OnDestroy')
        .dispatch({
          target: report.label.id,
        })
    }
  }

  public downloadClickHandler(): void {
    this._excelEcPlugin?.feature.actions.download
      .from('ReportDetails.Download.Button.Clicked')
      .dispatch('current')
  }

  // TODO: navigation between reports logic should be moved to some plugin, once
  // we switch to using new plugins
  /**
   * Handle clicking on previous button in page header navigator
   */
  public previousPageClickHandler(): void {
    this.reportPlugin.feature.actions.display
      .from('ReportDetails.Page.Pagination.PreviousButton')
      .dispatch('previous')
  }

  /**
   * Handle clicking on next button in page header navigator
   */
  public nextPageClickHandler(): void {
    this.reportPlugin.feature.actions.display
      .from('ReportDetails.Page.Pagination.NextButton')
      .dispatch('next')
  }
}
